import { useState, useCallback } from 'react';
import { Loader2, TrendingUp, TrendingDown, DollarSign, Calculator, Target, AlertTriangle, Clock, Truck, Receipt } from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { Modal } from '../ui';
import { useApiEffect } from '../../hooks';
import type { FinanceiroVeiculo } from '../../types';
import { formatCurrency } from '../../utils/format';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  veiculoId: number;
  veiculoNome: string;
}

export function FinanceiroVeiculoModal({ isOpen, onClose, veiculoId, veiculoNome }: Props) {
  useNotification(); // Hook required for useApiEffect

  const [financeiro, setFinanceiro] = useState<FinanceiroVeiculo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadData = useCallback(async (signal: AbortSignal) => {
    if (!veiculoId) return;
    setIsLoading(true);

    try {
      const response = await api.get<FinanceiroVeiculo>(`/veiculos/${veiculoId}/financeiro`, { signal });
      setFinanceiro(response.data);
    } finally {
      setIsLoading(false);
    }
  }, [veiculoId]);

  useApiEffect(loadData, [veiculoId], { enabled: isOpen && !!veiculoId });

  if (isLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={`Financeiro - ${veiculoNome}`} size="lg">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Modal>
    );
  }

  if (!financeiro) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={`Financeiro - ${veiculoNome}`} size="lg">
        <p className="text-center py-8 text-gray-500">Dados financeiros não disponíveis</p>
      </Modal>
    );
  }

  const lucroLiquido = financeiro.lucroLiquido ?? 0;
  const margemPositiva = lucroLiquido >= 0;
  const margemPercentual = financeiro.margemPercentual ?? 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Financeiro - ${veiculoNome}`} size="lg">
      <div className="space-y-6">
        {/* Resumo Principal */}
        <div className="grid grid-cols-2 gap-4">
          <div className={`p-4 rounded-lg ${margemPositiva ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center gap-2 mb-2">
              {margemPositiva ? (
                <TrendingUp className="w-5 h-5 text-green-600" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-600" />
              )}
              <span className="text-sm font-medium text-gray-700">
                {financeiro.vendido ? 'Lucro Líquido' : 'Lucro Estimado'}
              </span>
            </div>
            <p className={`text-2xl font-bold ${margemPositiva ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(lucroLiquido)}
            </p>
            <p className={`text-sm ${margemPositiva ? 'text-green-600' : 'text-red-600'}`}>
              {margemPercentual.toFixed(1)}% de margem
            </p>
          </div>

          <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-700">Custo Total</span>
            </div>
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(financeiro.custoTotalVeiculo)}</p>
            <p className="text-sm text-gray-500">Entrada + Custos + Despachante</p>
          </div>
        </div>

        {/* Dias no Estoque */}
        {financeiro.diasEstoque > 0 && (
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <Clock className="w-5 h-5 text-gray-500" />
            <span className="text-gray-700">
              <strong>{financeiro.diasEstoque}</strong> dias no estoque
            </span>
          </div>
        )}

        {/* Detalhamento de Valores */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Calculator className="w-4 h-4" />
            Detalhamento
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Valor de Compra</span>
                <span className="font-medium">{formatCurrency(financeiro.valorCompra)}</span>
              </div>
              {financeiro.valorFrete > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Frete</span>
                  <span className="font-medium">+ {formatCurrency(financeiro.valorFrete)}</span>
                </div>
              )}
              {financeiro.valorTaxas > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Taxas/Leilão</span>
                  <span className="font-medium">+ {formatCurrency(financeiro.valorTaxas)}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-gray-600 font-medium">Total Entrada</span>
                <span className="font-semibold">{formatCurrency(financeiro.totalEntrada)}</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Valor de Venda</span>
                <span className="font-medium text-green-600">{formatCurrency(financeiro.valorVenda)}</span>
              </div>
              {financeiro.valorMinimo && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Valor Mínimo</span>
                  <span className="font-medium">{formatCurrency(financeiro.valorMinimo)}</span>
                </div>
              )}
              {financeiro.valorFipe && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">FIPE</span>
                  <span className="font-medium">{formatCurrency(financeiro.valorFipe)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Custos Detalhados */}
        {financeiro.totalCustos > 0 && (
          <div className="bg-white border rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Receipt className="w-4 h-4" />
              Custos Adicionais
            </h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(financeiro.totalCustos)}</p>
                <p className="text-sm text-gray-500">Total</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(financeiro.custosPagos)}</p>
                <p className="text-sm text-gray-500">Pago</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-600">{formatCurrency(financeiro.custosPendentes)}</p>
                <p className="text-sm text-gray-500">Pendente</p>
              </div>
            </div>
            {financeiro.custosPorTipo && financeiro.custosPorTipo.length > 0 && (
              <div className="pt-3 border-t space-y-1">
                {financeiro.custosPorTipo.map((custo) => (
                  <div key={custo.tipo} className="flex justify-between text-sm">
                    <span className="text-gray-600">{custo.tipo} ({custo.quantidade})</span>
                    <span className="font-medium">{formatCurrency(custo.valor)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Despachante */}
        {financeiro.totalDespachante > 0 && (
          <div className="bg-white border rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Truck className="w-4 h-4" />
              Despachante
            </h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(financeiro.totalDespachante)}</p>
                <p className="text-sm text-gray-500">Total</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(financeiro.despachantePago)}</p>
                <p className="text-sm text-gray-500">Pago</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-600">{formatCurrency(financeiro.despachantePendente)}</p>
                <p className="text-sm text-gray-500">Pendente</p>
              </div>
            </div>
          </div>
        )}

        {/* Preço Sugerido */}
        <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">Preço Sugerido</h3>
          </div>
          <div className="flex items-center justify-center gap-4">
            <div className="text-center">
              <p className="text-sm text-gray-500">10%</p>
              <p className="text-lg font-bold text-blue-600">
                {formatCurrency(financeiro.precoSugeridoMargem10)}
              </p>
            </div>
            <div className="text-xl text-gray-300">→</div>
            <div className="text-center">
              <p className="text-sm text-gray-500">15%</p>
              <p className="text-lg font-bold text-purple-600">
                {formatCurrency(financeiro.precoSugeridoMargem15)}
              </p>
            </div>
            <div className="text-xl text-gray-300">→</div>
            <div className="text-center">
              <p className="text-sm text-gray-500">20%</p>
              <p className="text-lg font-bold text-green-600">
                {formatCurrency(financeiro.precoSugeridoMargem20)}
              </p>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center mt-2">
            Baseado no custo total ({formatCurrency(financeiro.custoTotalVeiculo)}) + margem
          </p>
        </div>

        {/* Alertas */}
        {margemPercentual < 5 && margemPercentual > 0 && (
          <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">Margem baixa</p>
              <p className="text-sm text-yellow-700">
                A margem de lucro está abaixo de 5%. Considere revisar o preço de venda ou reduzir os custos.
              </p>
            </div>
          </div>
        )}

        {!margemPositiva && (
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-800">Prejuízo projetado</p>
              <p className="text-sm text-red-700">
                O preço de venda atual está abaixo do custo total. A venda resultará em prejuízo de{' '}
                {formatCurrency(Math.abs(lucroLiquido))}.
              </p>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
